import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  computed,
  inject,
} from '@angular/core';

import { combineLatest, distinctUntilChanged, filter, map, Observable, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  Cabin,
  Category,
  FinnairAmount,
  FinnairItineraryItemFlight,
  SeatInfo,
  SeatmapSvgAndControlData,
  SeatPrice,
} from '@fcom/dapi/api/models';
import { AIRCRAFT_TRANSLATION_MAP } from '@fcom/common/interfaces/seat-map.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import {
  ButtonSize,
  ChipTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  ModalButtons,
  TagTheme,
} from '@fcom/ui-components';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { isPresent, uniqueBy } from '@fcom/core/utils';
import { CmsArticle } from '@fcom/core-api';
import { FragmentService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces';

import { bundleServiceMatchesForSeatSelector, CommonBookingFeatureState, seatMapForFlight } from '../../../../../store';
import { CampaignService } from '../../../../../services/campaign.service';
import { getSeatMapFlight, getSeatType } from '../../../utils';
import { SeatMapTraveler, seatTypeInfo } from '../../../interfaces';

export interface SeatChipData {
  article$: Observable<CmsArticle>;
  className: string;
  label: string;
  icon: string;
  originalPrice: FinnairAmount;
  price: FinnairAmount;
  seat: SeatInfo;
  seatType: {
    className: string;
    label: string;
    cmsKey: string;
  };
  bundle?: Category;
}

@Component({
  selector: 'fin-seat-map-seatchips',
  templateUrl: './seat-map-seatchips.component.html',
  styleUrls: ['./seat-map-seatchips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SeatMapSeatChipsComponent implements OnInit {
  private fragmentService = inject(FragmentService);
  private gtmService = inject(GtmService);
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  protected getIconForServiceCategory = getIconForServiceCategory;
  private campaignService = inject(CampaignService);
  private themeService = inject(FinnairThemeService);

  protected readonly seatMapThemeClass: Signal<string> = computed(() => {
    const theme = this.themeService.siteTheme();
    return theme === FinnairTheme.LEGACY ? 'legacy-theme' : theme;
  });

  @Input({ required: true }) contextCategory: Category;
  @Input() flight: FinnairItineraryItemFlight;
  @Input() activePassenger$: Observable<SeatMapTraveler>;
  @Input() includeBabyBassinetSeats: boolean;
  @Input() selectedSeats$: Observable<string[]>;
  @Input() displayBundles$: Observable<Category>;
  @Input() seatMapElement: ElementRef;

  @Output() scrollToSeat: EventEmitter<string> = new EventEmitter();

  availableSeatTypes$: Observable<SeatChipData[]>;
  availableSeatTypesInModal: SeatChipData[];
  showPersonalizedDiscountTag$: Observable<string | null>;

  // Filtered to be present, mirroring the previous flight.seatMap$ input (which never emitted before load).
  protected seatMap$: Observable<SeatmapSvgAndControlData>;

  readonly ButtonSize = ButtonSize;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;
  readonly TagTheme = TagTheme;

  seatIconCache: Record<string, string> = {};
  seatTypeModalOpen = false;

  ngOnInit(): void {
    this.showPersonalizedDiscountTag$ = this.activePassenger$.pipe(
      map((pax) => pax.id),
      distinctUntilChanged(),
      switchMap((paxId) =>
        this.campaignService.getPersonalizedCampaignTag$(this.flight.id, this.contextCategory, paxId)
      )
    );

    this.seatMap$ = this.store$.select(seatMapForFlight(this.flight.id)).pipe(filter(isPresent));

    this.availableSeatTypes$ = combineLatest([
      this.activePassenger$,
      this.seatMap$,
      this.selectedSeats$,
      this.displayBundles$,
      this.store$.select(bundleServiceMatchesForSeatSelector),
    ]).pipe(
      map(([pax, seatMap, selectedSeats, displayBundles, seatToBundleMatcher]) => {
        let seatTypeChips: SeatChipData[] = [];
        const bundlesBySeatNumber = seatToBundleMatcher[this.flight.id]?.[pax.id]?.[displayBundles] || {};

        seatMap?.seats
          ?.filter((seat) => {
            if (seat.statuses?.[pax.id] !== 'available') {
              return false;
            }

            return !displayBundles || !!bundlesBySeatNumber[seat.seatNumber];
          })
          .forEach((seat) => {
            const seatType = getSeatType(seat.characteristics);
            const seatPrice = displayBundles
              ? {
                  originalPrice: bundlesBySeatNumber[seat.seatNumber].originalPrice,
                  price: bundlesBySeatNumber[seat.seatNumber].totalPrice,
                  travelerIds: [pax.id],
                }
              : seat?.prices?.find((p: SeatPrice) => p.travelerIds.indexOf(pax.id) !== -1);

            const seatsOfSameTypeAndPrice = seatTypeChips.filter((addedSeat) => {
              const isSameSeatType = addedSeat.seatType.className === seatType.className;

              if (!isSameSeatType) {
                return false;
              }

              return addedSeat.price?.amount === seatPrice?.price?.amount;
            });

            if (seatsOfSameTypeAndPrice.length) {
              seatTypeChips = this.showSelectedSeatOnlyWhenLastOneOfThatTypeAndPrice(
                seat,
                seatType,
                seatPrice,
                selectedSeats,
                seatTypeChips
              );
            } else {
              seatTypeChips.push({
                ...seatType,
                article$: this.fragmentService.getFragment<CmsArticle>(this.getFragmentUrl(seatMap, seatType.cmsKey)),
                icon: this.getIconFromSeatMap(seat),
                price: seatPrice?.price,
                originalPrice: seatPrice.originalPrice,
                seat,
                seatType,
                bundle: displayBundles,
              });
            }
          });

        if (!this.includeBabyBassinetSeats) {
          seatTypeChips = seatTypeChips.filter((chip) => chip.seatType.className !== seatTypeInfo.B.className);
        }

        // First, sort by type and price
        seatTypeChips.sort((a, b) => {
          if (a.className === b.className) {
            return parseFloat(a.price.amount) - parseFloat(b.price.amount);
          }
          return a.className.localeCompare(b.className);
        });

        // Calculate the minimum price for each type
        const minPriceByType = this.findMinPriceByType(seatTypeChips);

        // Sort the array based on the type and price
        seatTypeChips.sort((a, b) => {
          if (minPriceByType[a.className] === minPriceByType[b.className]) {
            return a.className.localeCompare(b.className);
          }
          return Number(minPriceByType[a.className]) - Number(minPriceByType[b.className]);
        });

        this.availableSeatTypesInModal = seatTypeChips.filter(uniqueBy((seatTypeChip) => seatTypeChip.className));

        return seatTypeChips;
      })
    );
  }

  findMinPriceByType(arr: SeatChipData[]): Record<string, string> {
    return arr.reduce((minPriceByType, item) => {
      if (!minPriceByType[item.className] || item.price.amount < minPriceByType[item.className]) {
        minPriceByType[item.className] = item.price.amount;
      }
      return minPriceByType;
    }, {});
  }

  /**
   * For performance reasons we pass in the seatType and seatPrice, so we don't figure out the values again.
   *
   * Because this is called for every seat (so for example 300 seats on the A350) it can be quite heavy.
   */
  showSelectedSeatOnlyWhenLastOneOfThatTypeAndPrice(
    seat: SeatInfo,
    seatType: SeatChipData['seatType'],
    seatPrice: SeatPrice,
    selectedSeats: string[],
    seatTypeChips: SeatChipData[]
  ): SeatChipData[] {
    const seatToReplace = seatTypeChips.find((seatTypeChip) => {
      const isSameSeatType = seatTypeChip.seatType.className === seatType.className;

      return (
        isSameSeatType &&
        seatTypeChip.price?.amount === seatPrice?.price?.amount &&
        selectedSeats.includes(seatTypeChip.seat.seatNumber)
      );
    });

    if (!seatToReplace) {
      return seatTypeChips;
    }

    return seatTypeChips.map((seatTypeChip) => {
      if (seatToReplace.seat.seatNumber === seatTypeChip.seat.seatNumber) {
        seatToReplace.seat = seat;
        seatToReplace.icon = this.getIconFromSeatMap(seat);
      }

      return seatTypeChip;
    });
  }

  /**
   * Extract from the main SVG a copy of the seat icon for a specific seat.
   *
   * Removes the A tag from the seat and keeps everything else.
   * Removes xy coordinates from the seat element.
   */
  getIconFromSeatMap(seat: SeatInfo): string {
    if (this.seatIconCache[seat.seatNumber]) {
      return this.seatIconCache[seat.seatNumber];
    }

    const element: SVGGElement = this.seatMapElement.nativeElement
      .querySelector(`[data-id="${seat.seatNumber}"]`)
      .cloneNode(true);

    const aTag = element.querySelector('a');

    Array.from(aTag.childNodes).forEach((node) => {
      if ((node as SVGGElement).classList.contains('seat-selected')) {
        return;
      }

      element.append(node);
    });
    element.removeAttribute('transform');
    element.removeChild(aTag);

    this.seatIconCache[seat.seatNumber] = element.outerHTML;

    return this.seatIconCache[seat.seatNumber];
  }

  handleClick(seatData: SeatChipData): void {
    this.scrollToSeat.emit(seatData.seat.seatNumber);
    this.gtmService.seatMapEvent(
      `seat-type-${seatData.seatType.cmsKey}`,
      ElementTypes.BUTTON,
      'chip',
      ElementActions.CLICK,
      {
        flightInfo: getSeatMapFlight(this.flight),
      }
    );
  }

  openModal(): void {
    this.seatTypeModalOpen = true;
    this.gtmService.seatMapEvent('seat-type-info-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  closeModal(): void {
    this.seatTypeModalOpen = false;
  }

  private getFragmentUrl(seatMap: SeatmapSvgAndControlData, characteristicKey: string): string {
    const cabinClass = Cabin[this.flight.cabinClass];
    const aircraftType = AIRCRAFT_TRANSLATION_MAP[seatMap.aircraftCode];
    return `fragments.seatTypeInfos.${aircraftType}.${cabinClass.toLowerCase()}.${characteristicKey}.url`;
  }
}
